/**
 * A grade de um mês da escala.
 *
 * Papel: desenha a linha dos dias da semana e uma célula por dia da grade, com
 * o número do dia e as bandeiras das situações no topo, e abaixo, nesta ordem,
 * o aviso de feriado, quem está em plantão e de folga, quem visita, o texto de
 * `extras`, a anotação do dia e quem está de férias.
 *
 * Como usar: quem monta a grade passa o mês, o primeiro dia da semana e os três
 * conjuntos já carregados — dias, ausências e visitas — no recorte da grade
 * inteira, e o cruzamento acontece aqui. As células dos meses vizinhos mostram
 * o que está gravado nelas sobre o fundo cinza, porque a escala se monta por
 * semana e a semana da virada precisa aparecer inteira; elas não respondem ao
 * clique nem levam contorno de pendência, porque o dia se edita no mês dele.
 *
 * O dia marcado como previsão ou a definir leva o selo `PREVISÃO`, `A DEFINIR`
 * ou `PREV · A DEFINIR` no canto de baixo.
 *
 * O tom da célula vem da combinação de situações, e a combinação mais comum —
 * `familia` com `todos_residencia` — tem tom próprio, com a borda esquerda
 * marcando a segunda. Sem isso, os dois dias mais frequentes da escala ficariam
 * indistinguíveis. O dia de hoje troca só a cor da borda, e por isso também a
 * da faixa esquerda dessa combinação. O cursor sobre a célula só acende a borda
 * de um dia sem situação e que não é hoje: nos outros, a borda já é o que diz
 * a situação, e trocá-la sob o cursor faria o dia parecer de outra cor.
 *
 * O feriado de calendário aparece como aviso e como número em vermelho apenas
 * quando o dia **não** tem a situação `feriado` marcada à mão: os dois juntos
 * diriam a mesma coisa duas vezes na mesma célula.
 *
 * O contorno de pendência sai de `pendenciasDaEscala`, que não acusa nada num
 * dia em que ninguém foi escalado. Um dia sem registro também não acusa, porque
 * `daCasa` chega vazio a ela nesse caso: um mês ainda não montado marcaria
 * todas as células de uma vez, e o aviso que aparece em toda parte deixa de ser
 * aviso.
 */
import {
  type Ausencia,
  type DiaCalendario,
  type Visita,
  cobreODia,
  feriadoDoDia,
} from '@concierge/shared';

import { cn } from '@/shared/lib/utils';
import { comoChamar, pendenciasDaEscala } from '../escala';
import {
  type CelulaDaGrade,
  type MesMostrado,
  type PrimeiroDia,
  celulasDoMes,
  diasDaSemanaNaGrade,
  rotuloDoMes,
} from '../grade';

interface GradeDoMesProps {
  mostrado: MesMostrado;
  /** O dia em que cada semana começa. */
  primeiroDia: PrimeiroDia;
  dias: DiaCalendario[];
  ausencias: Ausencia[];
  visitas: Visita[];
  /** Quem trabalha na casa, para a grade saber de quem cobrar situação. */
  daCasa: string[];
  /** A data de hoje em `AAAA-MM-DD`, cuja célula ganha a borda de destaque. */
  hoje: string;
  /** Chamado com a data em `AAAA-MM-DD` da célula clicada. */
  onEscolherDia?: (dia: string) => void;
}

/** O tom de fundo e de borda de cada combinação de situações. */
const TOM_DA_CELULA = {
  'familia-todos': 'border-folha-familia-borda border-l-4 border-l-sucesso bg-folha-familia',
  familia: 'border-folha-familia-borda bg-folha-familia',
  todos: 'border-sucesso bg-sucesso/10',
  'folga-total': 'border-aviso bg-aviso/10',
  feriado: 'border-perigo bg-perigo/10',
  vazio: 'border-linha-forte bg-superficie',
} as const;

/**
 * Escolhe o tom da célula a partir das situações do dia.
 *
 * A ordem das perguntas é a prioridade, e a primeira pergunta é a combinação:
 * testar `familia` antes dela faria o dia de família com todo mundo na casa
 * parecer um dia de família comum.
 *
 * @param status - As situações do dia, em qualquer ordem.
 * @returns A chave de `TOM_DA_CELULA` correspondente.
 */
function tomDoDia(status: DiaCalendario['status']): keyof typeof TOM_DA_CELULA {
  const familia = status.includes('familia');
  const todos = status.includes('todos_residencia');

  if (familia && todos) return 'familia-todos';
  if (familia) return 'familia';
  if (todos) return 'todos';
  if (status.includes('folga_total')) return 'folga-total';
  if (status.includes('feriado')) return 'feriado';
  return 'vazio';
}

/** A bandeira de cada situação: o texto, que o CSS põe em caixa alta, e o fundo. */
const BANDEIRA_DO_STATUS = {
  familia: { rotulo: 'Família', fundo: 'bg-folha-familia-selo' },
  todos_residencia: { rotulo: 'Todos', fundo: 'bg-sucesso' },
  folga_total: { rotulo: 'Folga TODOS', fundo: 'bg-aviso' },
  feriado: { rotulo: 'Feriado', fundo: 'bg-perigo' },
} as const;

/** A ordem fixa das bandeiras, qualquer que seja a ordem de `status`. */
const ORDEM_DAS_BANDEIRAS = ['familia', 'todos_residencia', 'folga_total', 'feriado'] as const;

/**
 * A linha de uma lista de pessoas da célula, com o rótulo e uma etiqueta por nome.
 *
 * @param rotulo - `Res:` ou `Folga:`, que o CSS põe em caixa alta.
 * @param nomes - Os nomes curtos, na ordem em que aparecem.
 * @param tom - As cores da etiqueta: azul para plantão, âmbar para folga.
 * @returns A linha, ou nada quando a lista está vazia.
 */
function LinhaDeEtiquetas({
  rotulo,
  nomes,
  tom,
}: {
  rotulo: string;
  nomes: string[];
  tom: 'plantao' | 'folga';
}) {
  if (nomes.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-[3px]">
      <span className="font-mono text-[8.5px] tracking-[0.04em] text-texto-fraco uppercase">
        {rotulo}
      </span>
      {nomes.map((nome, indice) => (
        <span
          key={`${nome}-${String(indice)}`}
          className={cn(
            'inline-block rounded-[2px] px-[5px] py-px text-[9.5px] font-medium whitespace-nowrap',
            tom === 'plantao'
              ? 'bg-folha-etiqueta-plantao text-folha-familia-selo'
              : 'bg-aviso/18 text-aviso',
          )}
        >
          {nome}
        </span>
      ))}
    </div>
  );
}

/**
 * A grade do mês.
 *
 * @param mostrado - O ano e o mês desenhados; `rotuloDoMes` dele é o nome
 *   acessível da grade.
 * @param primeiroDia - O dia em que cada semana começa, 0 para domingo e 1
 *   para segunda.
 * @param dias - Os dias com registro da grade inteira; os demais aparecem em
 *   branco.
 * @param ausencias - As ausências que encostam no mês, cruzadas por data.
 * @param visitas - As visitas que encostam no mês, cruzadas por data.
 * @param daCasa - Os identificadores de quem trabalha na casa, para apurar quem
 *   ficou sem situação no dia.
 * @param hoje - A data de hoje em `AAAA-MM-DD`; num mês que não a contém,
 *   nenhuma célula ganha o destaque.
 * @param onEscolherDia - Chamado ao clicar numa célula do mês; ausente deixa a
 *   grade só de leitura, sem aparência de clicável.
 * @returns A linha de dias da semana e as semanas do mês.
 */
export function GradeDoMes({
  mostrado,
  primeiroDia,
  dias,
  ausencias,
  visitas,
  daCasa,
  hoje,
  onEscolherDia,
}: GradeDoMesProps) {
  const celulas = celulasDoMes(mostrado, primeiroDia);
  const porDia = new Map(dias.map((dia) => [dia.dia, dia]));

  return (
    <div role="group" aria-label={rotuloDoMes(mostrado)}>
      <div className="mb-1 grid grid-cols-7 gap-1">
        {diasDaSemanaNaGrade(primeiroDia).map((diaDaSemana) => (
          <div
            key={diaDaSemana.nome}
            className={cn(
              'px-1 py-1.5 text-center font-mono text-[10px] tracking-[0.06em] [overflow-wrap:anywhere] uppercase',
              diaDaSemana.fimDeSemana ? 'text-brass' : 'text-texto-fraco',
            )}
          >
            {diaDaSemana.nome}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {celulas.map((celula) => (
          <Celula
            key={celula.dia}
            celula={celula}
            dia={porDia.get(celula.dia)}
            ausencias={ausencias.filter((a) => cobreODia(a, celula.dia))}
            visitas={visitas.filter((v) => cobreODia(v, celula.dia))}
            daCasa={daCasa}
            eHoje={celula.dia === hoje}
            onEscolher={onEscolherDia}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * O texto do selo de previsão do dia, quando há marca.
 *
 * @param dia - O registro do dia.
 * @returns `PREVISÃO`, `A DEFINIR`, os dois juntos, ou `null` sem marca.
 */
function seloDePrevisao(dia: DiaCalendario | undefined): string | null {
  if (dia === undefined) return null;
  if (dia.previsao && dia.aDefinir) return 'PREV · A DEFINIR';
  if (dia.previsao) return 'PREVISÃO';
  if (dia.aDefinir) return 'A DEFINIR';
  return null;
}

/**
 * O que a célula escreve abaixo do número: a escala, as diárias, `extras`, a
 * anotação e as férias, nessa ordem.
 *
 * @param dia - O registro do dia, ou `undefined` num dia em branco.
 * @param ausencias - As ausências que cobrem o dia.
 * @param visitas - As visitas que cobrem o dia.
 * @returns As linhas da célula; nenhuma num dia sem nada.
 */
function DetalhesDoDia({
  dia,
  ausencias,
  visitas,
}: {
  dia: DiaCalendario | undefined;
  ausencias: Ausencia[];
  visitas: Visita[];
}) {
  const escalados = dia?.escalados ?? [];

  return (
    <>
      {escalados.length > 0 && (
        <div className="mt-0.5 flex flex-col gap-0.5 text-[10.5px] leading-[1.25]">
          <LinhaDeEtiquetas
            rotulo="Res:"
            nomes={escalados.filter((p) => p.papel === 'plantao').map(comoChamar)}
            tom="plantao"
          />
          <LinhaDeEtiquetas
            rotulo="Folga:"
            nomes={escalados.filter((p) => p.papel === 'folga').map(comoChamar)}
            tom="folga"
          />
        </div>
      )}

      {visitas.length > 0 && (
        <p className="mt-[3px] text-[10px] font-semibold text-sucesso">
          +{' '}
          {visitas
            .map((v) =>
              v.observacao === null ? v.visitante : `${v.visitante} ${v.observacao}`,
            )
            .join(' | ')}
        </p>
      )}
      {dia?.extras !== undefined && dia.extras !== null && (
        <p className="mt-[3px] text-[10px] font-semibold text-sucesso">{dia.extras}</p>
      )}
      {dia?.observacao !== undefined && dia.observacao !== null && (
        <p className="mt-[3px] text-[10px] text-texto-fraco italic">{dia.observacao}</p>
      )}
      {ausencias.length > 0 && (
        <p className="mt-[3px] text-[10px] text-texto-fraco italic">
          Férias: {ausencias.map((a) => comoChamar(a.colaborador)).join(', ')}
        </p>
      )}
    </>
  );
}

/**
 * As bandeiras das situações do dia, na ordem fixa.
 *
 * @param status - As situações do dia, em qualquer ordem.
 * @returns As bandeiras, ou nada num dia sem situação.
 */
function Bandeiras({ status }: { status: DiaCalendario['status'] }) {
  if (status.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-[3px]">
      {ORDEM_DAS_BANDEIRAS.filter((atual) => status.includes(atual)).map((atual) => (
        <span
          key={atual}
          className={cn(
            'rounded-[2px] px-[5px] py-px font-mono text-[8.5px] font-semibold tracking-[0.03em] whitespace-nowrap text-superficie uppercase',
            BANDEIRA_DO_STATUS[atual].fundo,
          )}
        >
          {BANDEIRA_DO_STATUS[atual].rotulo}
        </span>
      ))}
    </div>
  );
}

interface CelulaProps {
  celula: CelulaDaGrade;
  dia: DiaCalendario | undefined;
  ausencias: Ausencia[];
  visitas: Visita[];
  daCasa: string[];
  eHoje: boolean;
  onEscolher?: (dia: string) => void;
}

/**
 * Uma célula da grade.
 *
 * @param celula - A data e se ela pertence ao mês mostrado.
 * @param dia - O registro do dia, ou `undefined` num dia em branco.
 * @param ausencias - As ausências que cobrem este dia.
 * @param visitas - As visitas que cobrem este dia.
 * @param daCasa - Quem deveria ter situação definida no dia.
 * @param eHoje - Se a célula é a do dia de hoje.
 * @param onEscolher - Chamado ao clicar; ausente, ou em célula de mês vizinho,
 *   a célula não responde.
 * @returns A célula desenhada, como botão quando é clicável.
 */
function Celula({ celula, dia, ausencias, visitas, daCasa, eHoje, onEscolher }: CelulaProps) {
  const moldura =
    'relative flex min-h-[106px] flex-col overflow-hidden rounded-[4px] border p-[7px] text-left';
  const status = dia?.status ?? [];

  if (!celula.doMes) {
    return (
      <div className={cn(moldura, 'border-linha-forte bg-superficie-2 text-texto-fraco')}>
        <div className="mb-1 flex items-center justify-between gap-1">
          <span className="font-mono text-xs font-medium text-texto-medio">
            {celula.numero}
          </span>
          <Bandeiras status={status} />
        </div>
        <DetalhesDoDia dia={dia} ausencias={ausencias} visitas={visitas} />
      </div>
    );
  }

  const feriado = status.includes('feriado') ? null : feriadoDoDia(celula.dia);
  const selo = seloDePrevisao(dia);

  const pendencias = pendenciasDaEscala({
    status,
    escalados: dia?.escalados ?? [],
    deFerias: ausencias.map((a) => a.colaborador.id),
    daCasa: dia === undefined ? [] : daCasa,
  });
  const temPendencia = pendencias.length > 0;

  const conteudo = (
    <>
      <div className="mb-1 flex items-center justify-between gap-1">
        <span
          className={cn(
            'font-mono text-xs font-medium',
            feriado === null ? 'text-texto-medio' : 'text-perigo',
          )}
        >
          {celula.numero}
        </span>
        <Bandeiras status={status} />
      </div>

      {feriado !== null && (
        <p className="mb-[3px] font-mono text-[9px] leading-[1.2] text-perigo" title={feriado}>
          Feriado
        </p>
      )}

      <DetalhesDoDia dia={dia} ausencias={ausencias} visitas={visitas} />

      {selo !== null && (
        <span className="absolute right-[5px] bottom-1 rounded-[2px] bg-superficie/85 px-1 py-px font-mono text-[7.5px] font-semibold text-perigo">
          {selo}
        </span>
      )}

      {temPendencia && <span className="sr-only">Pendência na escala</span>}
    </>
  );

  const tom = cn(
    moldura,
    TOM_DA_CELULA[tomDoDia(status)],
    temPendencia && 'outline-2 -outline-offset-2 outline-aviso',
    eHoje && 'border-brass-light',
  );

  if (onEscolher === undefined) {
    return <div className={tom}>{conteudo}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => onEscolher(celula.dia)}
      className={cn(
        tom,
        'cursor-pointer transition-[border-color] duration-[120ms]',
        tomDoDia(status) === 'vazio' && !eHoje && 'hover:border-brass',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
      )}
    >
      {conteudo}
    </button>
  );
}
